import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
// helpers

import clsx from 'classnames';
import _ from 'lodash';

// ui
import { Card, CardContent, Grid, Divider, Box } from '@mui/material';
import ToggleButton from '@mui/lab/ToggleButton';
import { makeStyles } from '@mui/styles';
// platform helpers
import * as Yup from 'yup';
import { useLocation } from 'react-router-dom';
import { PubSub } from '../../../../utils/eventUtils';

// components
import { TextAreaBase } from '../../../inputs/TextAreaBase';
import { withFormController } from '../../../hocs/forms';
import {
  updateSecurityQuestions,
  updateOrgProfile,
  verifyOrgProfile,
} from '../../../../accounts/AccountService';
import CBButton from '../../../Buttons/CbButton';
import { InputLabelBase } from '../../../inputs/InputLabelBase';
import { EditSection } from '../../../../public/quote/confirm/EditSection';
import RenewalQuestions from '../../../../console/_global/renewals/RenewalQuestions';
import { ControlledToggleButtonGroup } from '../../../Buttons/ControlledToggleButtonGroup';
import { useAPIErrorHandler } from '../../../hooks/useAPIErrorHandler';
import { Modal } from '../../../modals/v2/helpers/v2.modal.helpers';
import { InvalidateQuotesConfig } from '../../../modals/InvalidateQuotes';
import { FirmoAndSecurityHeader } from '../../../../console/agencies/quotes/PrimeX/AccountDetails/components/SecurityAssessment';
import useNotifications from '../../../../_lib/notifications/useNotifications';

const TextArea = withFormController(TextAreaBase);

const P250SecurityQuestions = ({
  accountId,
  canVerify = false,
  data,
  from,
  readOnly = false,
  togglePrompt,
  isWithinExpiryPeriod = false,
  onSectionSubmit = _.noop,
  onSubmitHandler,
  ...props
}: any) => {
  const location = useLocation();
  const notifications = useNotifications();
  const handleAPIError = useAPIErrorHandler();

  const classes = useClasses({ from, readOnly });

  const ScopeSchema = isWithinExpiryPeriod
    ? Yup.bool()
        .required()
        .typeError('Change in scope is a required field')
        .label('Change in scope')
    : Yup.bool().nullable().label('Change in scope');

  const schema = Yup.object().shape({
    backupFrequency: Yup.string().required().label('Backup Frequency'),
    backupType: Yup.array().when('backupFrequency', {
      is: 'NEVER',
      then: Yup.array().label('Back-up Type'),
      otherwise: Yup.array()
        .min(1, 'Please select at least one back up option')
        .required()
        .label('Back-up Type'),
    }),
    backupFrequencyOther: Yup.string().when('backupType', {
      is: (backup) =>
        typeof backup !== 'undefined' &&
        backup.includes('backupFrequencyOther') === true,
      then: Yup.string()
        .required()
        .typeError('Back-up is a required field')
        .label('Back-up Type'),
      otherwise: Yup.string().nullable().label('Back-up Type'),
    }),
    mfaAuthentication: Yup.bool()
      .required()
      .label('MFA Authentication')
      .typeError('MFA Authentication is required'),
    mfaType: Yup.array().when('mfaAuthentication', {
      is: true || 'true',
      then: Yup.array()
        .min(1, 'Please select at least one MFA option')
        .required()
        .label('MFA Type'),
      otherwise: Yup.array().label('MFA Type'),
    }),
    mfaOther: Yup.string().when('mfaType', {
      is: (mfa) =>
        typeof mfa !== 'undefined' && mfa.includes('mfaOther') === true,
      then: Yup.string().when('mfaAuthentication', {
        is: true || 'true',
        then: Yup.string()
          .required()
          .typeError('MFA Other is a required field')
          .label('MFA Other'),
        otherwise: Yup.string()
          .nullable()
          .typeError('MFA Other is a required field!'),
      }),
      otherwise: Yup.string()
        .nullable()
        .typeError('MFA Other is a required field!'),
    }),
    incidentResponsePlan: Yup.bool()
      .required()
      .typeError('Incident Response Plan is a required field')
      .label('Incident Response Plan'),
    changeInScope: ScopeSchema,
    changeInScopeDetail: Yup.string().when('changeInScope', {
      is: true || 'true',
      then: Yup.string()
        .required()
        .typeError('Change in Scope is a required field')
        .label('Change in Scope Details'),
      otherwise: Yup.string().label('Change in Scope Plan'),
    }),
  });

  const orgProfile = from === 'orgProfile';
  const screen = location.pathname.split('/')[1];

  const customer = screen === 'customer';
  const [editing, setEditing] = React.useState(!!orgProfile);

  const defaultValues = React.useMemo(
    () =>
      getDefaultValues({
        accountData: data ?? {},
      }),
    [data]
  );

  const { handleSubmit, ...methods } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const {
    formState: { isDirty, isSubmitting, errors },
    control,
    reset,
  } = methods;

  const values = methods.getValues();
  // Watched, not taken off the getValues() snapshot above: the toggle is a
  // Controller, so answering it re-renders only that field. The gate below
  // currently survives that only via this component's formState.isDirty
  // subscription -- watching the flag makes it depend on the flag itself.
  const pastCyberIncident = methods.watch('pastCyberIncident');

  React.useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  React.useEffect(() => {
    const sub = PubSub.subscribe('cancel-detailChanges', (res: any) => {
      if (res) {
        reset(defaultValues);
      }
    });
    const sub2 = PubSub.subscribe(
      'security-questions:save',
      (nextValues: any) => {
        if (nextValues) {
          methods.reset({
            ...methods.getValues(),
            backupFrequency: _.get(
              nextValues,
              'p250Details.backupFrequency',
              null
            ),
          });
        }
      }
    );

    return () => {
      sub.remove();
      sub2.remove();
    };
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    PubSub.publish('org:agent-attested', {
      isVerifiedByAgent: false,
    });
  }, [methods.formState, values]);

  const updatingQuestions = (payload: any) => {
    updateSecurityQuestions(
      {},
      {
        ...payload,
      }
    )
      .then(() => {
        notifications.show({
          type: 'success',
          title: 'Security questions saved.',
          position: 'bottom-right',
        });

        if (!orgProfile) {
          setEditing(false);
          PubSub.publish('security-questions:save', payload);
          PubSub.publish('disable-Submit', false);
          PubSub.publish('fetch-details', true);
          PubSub.publish('org:agent-attested', {
            isVerifiedByAgent: false,
          });
        }

        if (typeof onSectionSubmit === 'function') {
          onSectionSubmit();
        }
      })
      .catch(
        handleAPIError(
          'Unable to update questions at this time. Please try again in sometime.'
        )
      );
  };

  const updateOrganisationProfile = (payload: any) => {
    updateOrgProfile({}, payload)
      .then((resp: any) => {
        notifications.success('Security Assessment Updated Successfully.');
        if (from !== 'tabs') {
          reset({
            ...values,
            pastCyberIncidentDetails: _.get(
              payload,
              'p250Details.pastCyberIncidentDetails',
              ''
            ),
          });
        }

        if (typeof onSectionSubmit === 'function') {
          onSectionSubmit();
        }
      })
      .catch((error: any) => {
        reset(defaultValues);
        handleAPIError(
          'Unable to update profile at this time. Please try again in sometime.'
        )(error);
      });
  };

  const onSubmit = ({
    isSecurityOfficer,
    isSecurityTraining,
    useEncryption,
    useCloudStorage,
    backupFrequency,
    patchingFrequency,
    pastCyberIncident,
    claimHistory,
    civilOrCriminalAction,
    pendingLitigation,
    lossInBusinessIncome,
    securityBreachRequiringNotification,
    ...formData
  }: any) => {
    const payload = {
      isVerifyingBankAccounts: _.get(data, 'isVerifyingBankAccounts', null),
      isAuthenticatingFundTransferRequests: _.get(
        data,
        'isAuthenticatingFundTransferRequests',
        null
      ),
      isPreventingUnauthorizedWireTransfers: _.get(
        data,
        'isPreventingUnauthorizedWireTransfers',
        null
      ),
      isSecurityOfficer,
      isSecurityTraining,
      useEncryption,
      useCloudStorage,
      claimHistory,
      mfaAuthentication: formData.mfaAuthentication,
      mfaEmail:
        formData.mfaAuthentication === true
          ? formData.mfaType.includes('mfaEmail')
          : false,
      mfaCloudDeployments:
        formData.mfaAuthentication === true
          ? formData.mfaType.includes('mfaCloudDeployments')
          : false,
      mfaMissionCriticalSystems: formData.mfaAuthentication
        ? formData.mfaType.includes('mfaMissionCriticalSystems')
        : false,
      mfaRemoteAccess:
        formData.mfaAuthentication === true
          ? formData.mfaType.includes('mfaRemoteAccess')
          : false,
      mfaOther: formData.mfaAuthentication !== true ? '' : formData.mfaOther,
      incidentResponsePlan: formData.incidentResponsePlan,
      changeInScope: formData.changeInScope,
      changeInScopeDetail: formData.changeInScopeDetail,
      renewalChangeInAnswers: formData.renewalChangeInAnswers,
      priorCoverage: formData.priorCoverage,
      p250Details: {
        backupFrequency,
        patchingFrequency,
        pastCyberIncident,
        civilOrCriminalAction,
        pendingLitigation,
        lossInBusinessIncome,
        securityBreachRequiringNotification,
        dmzSeparation: data.p250Details?.dmzSeparation,
        testedFullFailover: data.p250Details?.testedFullFailover,
        thirdPartySecurityAgreement:
          data.p250Details?.thirdPartySecurityAgreement,
        pastCyberIncidentDetails: formData.pastCyberIncidentDetails,
        backupFrequencyEncrypted:
          backupFrequency !== 'NEVER'
            ? formData.backupType.includes('backupFrequencyEncrypted')
            : false,
        backupFrequencyTested:
          backupFrequency !== 'NEVER'
            ? formData.backupType.includes('backupFrequencyTested')
            : false,
        backupFrequencySeparate:
          backupFrequency !== 'NEVER'
            ? formData.backupType.includes('backupFrequencySeparate')
            : false,
        backupFrequencyOther:
          formData.backupType !== 'NEVER' ? formData.backupFrequencyOther : '',
      },
      accountId,
    };

    if (typeof onSubmitHandler == 'function') {
      return onSubmitHandler(payload);
    }

    if (!orgProfile && isDirty && customer) {
      setEditing(false);
      verifyOrgProfile({}, payload)
        .then((res: any) => {
          const { WillInvalidateQuotes, WillReferQuotes } = res.data;
          if (WillReferQuotes || WillInvalidateQuotes) {
            Modal.show(InvalidateQuotesConfig, {
              data: {
                update: 'questions',
                reset,
                ...payload,
              },
              config: {
                title: `Account Changes for ${data.name}`,
              },
            });
          } else {
            updatingQuestions(payload);
          }
        })
        .catch(
          handleAPIError(
            'Unable to verify profile at this time. Please try again in sometime.'
          )
        );
    } else if (orgProfile) {
      updateOrganisationProfile(payload);
    } else {
      updatingQuestions(payload);
    }
  };

  const handleCancel = () => {
    reset(defaultValues);
  };

  const verifyIcon = 'VerifiedTabYes.svg';
  const verifyElement = canVerify ? (
    <div
      role="button"
      tabIndex={0}
      className={classes.verifyTabIcon}
      data-testid="verify-btn"
    >
      <img src={`images/${verifyIcon}`} alt="" className={classes.iconStyle} />
    </div>
  ) : null;

  React.useEffect(() => {
    if (togglePrompt) {
      togglePrompt('P250_SECURITY_QUESTIONS', isDirty);
    }
  }, [isDirty, togglePrompt]);

  const live = from === 'tab' && !readOnly && !editing;
  const pastIncidentDetailsStyle = clsx(
    classes.text,
    classes.pastIncidentDetails
  );

  const toggleButtonProps = {
    disabled: readOnly,
    className: classes.toggleButton,
    classes: {
      disabled: classes.toggleButtonDisabled,
    },
  };

  if (!data) {
    return null;
  }

  return (
    // @ts-expect-error TS(2741): Property 'handleSubmit' is missing in type '{ chil... Remove this comment to see the full error message
    <FormProvider {...methods}>
      <Card
        // @ts-expect-error TS(2339): Property 'card' does not exist on type 'ClassNameM... Remove this comment to see the full error message
        className={orgProfile ? classes.cardContainer : classes.card}
        style={{ overflow: 'visible' }}
      >
        <CardContent
          className={clsx({
            [classes.cardContentContainer]: orgProfile,
          })}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            {!orgProfile && (
              <div className="flex--spaced">
                <FirmoAndSecurityHeader>
                  Security Assessment
                </FirmoAndSecurityHeader>
              </div>
            )}
            <section className={orgProfile && classes.inactiveBackground}>
              <Grid container justifyContent="left">
                <Grid
                  container
                  justifyContent="left"
                  className={
                    orgProfile ? classes.inputFields : classes.attestInput
                  }
                >
                  <Grid item md={12}>
                    <div>
                      <InputLabelBase
                        TypographyProps={{
                          variant: 'body2',
                          gutterBottom: true,
                        }}
                        style={{ paddingTop: 0 }}
                      >
                        Does the organization currently carry an insurance
                        policy that insures against a Cyber loss?
                      </InputLabelBase>
                      <ControlledToggleButtonGroup
                        name="priorCoverage"
                        exclusive
                        data-testid="prior-coverage-input-group"
                      >
                        <ToggleButton value="YES" {...toggleButtonProps}>
                          <div>Yes</div>
                        </ToggleButton>
                        <ToggleButton value="NO" {...toggleButtonProps}>
                          <div>No</div>
                        </ToggleButton>
                        <ToggleButton value="UNCERTAIN" {...toggleButtonProps}>
                          <div>Uncertain</div>
                        </ToggleButton>
                      </ControlledToggleButtonGroup>
                    </div>
                  </Grid>
                  <Grid item md={12}>
                    <div>
                      <InputLabelBase {...LabelProps}>
                        Had any past Cyber Incidents?
                      </InputLabelBase>
                      <ControlledToggleButtonGroup
                        name="pastCyberIncident"
                        exclusive
                        data-testid="past-cyber-incident-input-group"
                      >
                        <ToggleButton value {...toggleButtonProps}>
                          <div>Yes</div>
                        </ToggleButton>
                        <ToggleButton value={false} {...toggleButtonProps}>
                          <div>No</div>
                        </ToggleButton>
                      </ControlledToggleButtonGroup>
                    </div>
                  </Grid>
                  <Grid item md={12}>
                    <div>
                      <InputLabelBase {...LabelProps} required>
                        Does your organization have a Designated Information
                        Security Contact?
                      </InputLabelBase>
                      <ControlledToggleButtonGroup
                        name="isSecurityOfficer"
                        exclusive
                        data-testid="security-officer-input-group"
                      >
                        <ToggleButton value {...toggleButtonProps}>
                          <div>Yes</div>
                        </ToggleButton>
                        <ToggleButton value={false} {...toggleButtonProps}>
                          <div>No</div>
                        </ToggleButton>
                      </ControlledToggleButtonGroup>
                    </div>
                  </Grid>
                  <Grid item md={12}>
                    <Box width="60%">
                      <InputLabelBase {...LabelProps}>
                        Does the organization provide mandatory information
                        security training to all employees at least annually? If
                        not, are they willing to implement it during the policy
                        period?
                      </InputLabelBase>
                      <ControlledToggleButtonGroup
                        name="isSecurityTraining"
                        exclusive
                        data-testid="security-training-input-group"
                      >
                        <ToggleButton value {...toggleButtonProps}>
                          <div>Yes</div>
                        </ToggleButton>
                        <ToggleButton value={false} {...toggleButtonProps}>
                          <div>No</div>
                        </ToggleButton>
                      </ControlledToggleButtonGroup>
                    </Box>
                  </Grid>
                  <Grid item md={12}>
                    <div>
                      <InputLabelBase {...LabelProps}>
                        Does the organization encrypt emails, mobile and
                        computing devices containing sensitive information
                        (e.g., PII, PHI, PCI) sent to external parties?
                      </InputLabelBase>
                      <ControlledToggleButtonGroup
                        name="useEncryption"
                        exclusive
                        data-testid="use-encryption-input-group"
                      >
                        <ToggleButton value {...toggleButtonProps}>
                          <div>Yes</div>
                        </ToggleButton>
                        <ToggleButton value={false} {...toggleButtonProps}>
                          <div>No</div>
                        </ToggleButton>
                      </ControlledToggleButtonGroup>
                    </div>
                  </Grid>
                  <Grid item md={12}>
                    <div>
                      <InputLabelBase {...LabelProps}>
                        Does the organization have sensitive information stored
                        on the cloud?
                      </InputLabelBase>
                      <ControlledToggleButtonGroup
                        name="useCloudStorage"
                        exclusive
                        data-testid="use-cloud-storage-input-group"
                      >
                        <ToggleButton value {...toggleButtonProps}>
                          <div>Yes</div>
                        </ToggleButton>
                        <ToggleButton value={false} {...toggleButtonProps}>
                          <div>No</div>
                        </ToggleButton>
                      </ControlledToggleButtonGroup>
                    </div>
                  </Grid>
                  <Grid item md={12}>
                    <div>
                      <InputLabelBase {...LabelProps}>
                        How often does the organization apply updates to
                        critical IT-systems and applications?
                      </InputLabelBase>
                      <ControlledToggleButtonGroup
                        name="patchingFrequency"
                        exclusive
                        data-testid="patching-frequency-input-group"
                      >
                        <ToggleButton value="WEEKLY" {...toggleButtonProps}>
                          <div>Weekly or more</div>
                        </ToggleButton>
                        <ToggleButton value="MONTHLY" {...toggleButtonProps}>
                          <div>Monthly</div>
                        </ToggleButton>
                        <ToggleButton value="QUARTERLY" {...toggleButtonProps}>
                          <div>Quarterly</div>
                        </ToggleButton>
                        <ToggleButton value="SIX_MONTHS" {...toggleButtonProps}>
                          <div>Every 6 Months</div>
                        </ToggleButton>
                        <ToggleButton value="NEVER" {...toggleButtonProps}>
                          <div>Never</div>
                        </ToggleButton>
                      </ControlledToggleButtonGroup>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
              <div style={{ marginTop: '1.5rem' }}>
                <RenewalQuestions
                  isWithinExpiryPeriod={isWithinExpiryPeriod}
                  readOnly={readOnly}
                  cardStyle={props.cardStyle}
                  methods={methods}
                  accountId={accountId}
                />
              </div>
              {pastCyberIncident && (
                <>
                  <Divider className={classes.divider} />
                  <h3 className={classes.text}>Past Incidents</h3>
                  <Grid
                    container
                    justifyContent="left"
                    className={
                      orgProfile ? classes.inputFields : classes.attestInput
                    }
                  >
                    <Grid item md={12}>
                      <div>
                        <InputLabelBase
                          {...LabelProps}
                          style={{ paddingTop: 0 }}
                        >
                          Has the Organization filed any claims due to a cyber
                          event?
                        </InputLabelBase>
                        <ControlledToggleButtonGroup
                          name="claimHistory"
                          exclusive
                          data-testid="claim-history-input-group"
                        >
                          <ToggleButton value={0} {...toggleButtonProps}>
                            <div>Never</div>
                          </ToggleButton>
                          <ToggleButton value={1} {...toggleButtonProps}>
                            <div>Within last 12 months</div>
                          </ToggleButton>
                          <ToggleButton value={2} {...toggleButtonProps}>
                            <div>Within last 2 years</div>
                          </ToggleButton>
                          <ToggleButton value={3} {...toggleButtonProps}>
                            <div>Within last 3 years</div>
                          </ToggleButton>
                          <ToggleButton value={4} {...toggleButtonProps}>
                            <div>Within last 4 years</div>
                          </ToggleButton>
                          <ToggleButton value={5} {...toggleButtonProps}>
                            <div>within 5 years or more</div>
                          </ToggleButton>
                        </ControlledToggleButtonGroup>
                      </div>
                    </Grid>
                    <Grid item md={12}>
                      <div>
                        <InputLabelBase {...LabelProps}>
                          Has the organization ever been a party to a Civil or
                          criminal action or administrative proceeding alleging
                          violation of any federal, state, local or common law?
                        </InputLabelBase>
                        <ControlledToggleButtonGroup
                          name="civilOrCriminalAction"
                          exclusive
                          data-testid="civil-criminal-action-input-group"
                        >
                          <ToggleButton value {...toggleButtonProps}>
                            <div>Yes</div>
                          </ToggleButton>
                          <ToggleButton value={false} {...toggleButtonProps}>
                            <div>No</div>
                          </ToggleButton>
                        </ControlledToggleButtonGroup>
                      </div>
                    </Grid>
                    <Grid item md={12}>
                      <div>
                        <InputLabelBase {...LabelProps}>
                          Is there currently any pending litigation,
                          administrative proceeding or claim against the named
                          applicant, organization and/or any of the prospective
                          insureds?
                        </InputLabelBase>
                        <ControlledToggleButtonGroup
                          name="pendingLitigation"
                          exclusive
                          data-testid="pending-litigation-input-group"
                        >
                          <ToggleButton value {...toggleButtonProps}>
                            <div>Yes</div>
                          </ToggleButton>
                          <ToggleButton value={false} {...toggleButtonProps}>
                            <div>No</div>
                          </ToggleButton>
                        </ControlledToggleButtonGroup>
                      </div>
                    </Grid>
                    <Grid item md={12}>
                      <div>
                        <InputLabelBase {...LabelProps}>
                          During the last three years, has the organization
                          suffered loss of business income as a result of
                          unscheduled system downtime?
                        </InputLabelBase>
                        <ControlledToggleButtonGroup
                          name="lossInBusinessIncome"
                          exclusive
                          data-testid="loss-of-income-input-group"
                        >
                          <ToggleButton value {...toggleButtonProps}>
                            <div>Yes</div>
                          </ToggleButton>
                          <ToggleButton value={false} {...toggleButtonProps}>
                            <div>No</div>
                          </ToggleButton>
                        </ControlledToggleButtonGroup>
                      </div>
                    </Grid>
                    <Grid item md={12}>
                      <div>
                        <InputLabelBase {...LabelProps}>
                          During the last three years, has the organization
                          suffered a security breach requiring customer or
                          third-party notification according to state or federal
                          regulations?
                        </InputLabelBase>
                        <ControlledToggleButtonGroup
                          name="securityBreachRequiringNotification"
                          exclusive
                          data-testid="security-breach-notification-input-group"
                        >
                          <ToggleButton value {...toggleButtonProps}>
                            <div>Yes</div>
                          </ToggleButton>
                          <ToggleButton value={false} {...toggleButtonProps}>
                            <div>No</div>
                          </ToggleButton>
                        </ControlledToggleButtonGroup>
                      </div>
                    </Grid>
                    <Grid item md={12}>
                      <Box width="100%">
                        <h3 className={pastIncidentDetailsStyle}>
                          Past Incident Details
                        </h3>
                        <TextArea
                          maxRows={4}
                          minRows={4}
                          name="pastCyberIncidentDetails"
                          className={classes.input}
                          multiline
                          // @ts-expect-error TS(2322): Type 'Control<{ pastCyberIncident: any; isSecurity... Remove this comment to see the full error message
                          controllerProps={{ control }}
                          placeholder={
                            editing
                              ? 'Enter past incident(s) / claim(s) details.'
                              : ''
                          }
                          blueText={live}
                          fullWidth
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}
              {!orgProfile && <EditSection show={!readOnly} />}
              {orgProfile && (
                <Grid
                  container
                  justifyContent="center"
                  className={classes.buttonContainer}
                >
                  <Grid item md={4} className={classes.leftBtn}>
                    <CBButton
                      size="large"
                      styleName="cancel"
                      style={buttonStyles}
                      onClick={handleCancel}
                      disabled={isSubmitting || !isDirty}
                      data-testid="cancel-btn"
                    >
                      Cancel
                    </CBButton>
                  </Grid>
                  <Grid item md={4}>
                    <CBButton
                      type="submit"
                      size="large"
                      styleName="ctaButton"
                      style={buttonStyles}
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      data-testid="submit-btn"
                    >
                      Save Changes
                    </CBButton>
                  </Grid>
                </Grid>
              )}
              {verifyElement}
            </section>
          </form>
        </CardContent>
      </Card>
    </FormProvider>
  );
};

const getDefaultValues = ({ accountData }: any) => {
  // necessary to message backend data for form
  function trueCheck(key: any) {
    return accountData[key] === true;
  }

  function trueCheckDetails(key: any) {
    return !!accountData.p250Details?.[key];
  }

  // need to flatten mfaValues & backupValues for defaulValue assign
  let mfaType = [
    'mfaEmail',
    'mfaCloudDeployments',
    'mfaMissionCriticalSystems',
    'mfaRemoteAccess',
  ];
  let backupType = [
    'backupFrequencyEncrypted',
    'backupFrequencyTested',
    'backupFrequencySeparate',
  ];

  mfaType = mfaType.filter(trueCheck);
  backupType = backupType.filter(trueCheckDetails);

  if (_.get(accountData, 'mfaOther', false)) {
    mfaType.push('mfaOther');
  }
  if (_.get(accountData, 'p250Details.backupFrequencyOther', false)) {
    backupType.push('backupFrequencyOther');
  }

  return {
    pastCyberIncident: _.get(
      accountData,
      'p250Details.pastCyberIncident',
      null
    ),
    isSecurityOfficer: _.get(accountData, 'isSecurityOfficer'),
    isSecurityTraining: _.get(accountData, 'isSecurityTraining'),
    backupFrequency: _.get(accountData, 'p250Details.backupFrequency', null),
    patchingFrequency: _.get(
      accountData,
      'p250Details.patchingFrequency',
      null
    ),
    useEncryption: _.get(accountData, 'useEncryption'),
    useCloudStorage: _.get(accountData, 'useCloudStorage'),
    claimHistory: _.get(accountData, 'claimHistory'),
    civilOrCriminalAction: _.get(
      accountData,
      'p250Details.civilOrCriminalAction',
      null
    ),
    pendingLitigation: _.get(
      accountData,
      'p250Details.pendingLitigation',
      null
    ),
    lossInBusinessIncome: _.get(
      accountData,
      'p250Details.lossInBusinessIncome',
      null
    ),
    securityBreachRequiringNotification: _.get(
      accountData,
      'p250Details.securityBreachRequiringNotification',
      null
    ),
    pastCyberIncidentDetails: _.get(
      accountData,
      'p250Details.pastCyberIncidentDetails',
      null
    ),
    mfaType,
    backupType,
    mfaOther: _.get(accountData, 'mfaOther', null),
    backupFrequencyOther: _.get(
      accountData,
      'p250Details.backupFrequencyOther',
      null
    ),
    mfaAuthentication: _.get(accountData, 'mfaAuthentication', null),
    incidentResponsePlan: _.get(accountData, 'incidentResponsePlan', null),
    changeInScope: _.get(accountData, 'changeInScope', null),
    changeInScopeDetail: _.get(accountData, 'changeInScopeDetail', ''),
    priorCoverage: _.get(accountData, 'priorCoverage', null),
  };
};

const buttonStyles = {
  width: '150px',
  margin: `1.66rem 0 1.66rem 0`,
};

const LabelProps = {
  required: true,
  TypographyProps: {
    variant: 'body2',
    gutterBottom: true,
  },
};

const useClasses = makeStyles(({ palette, config, spacing }) => {
  return {
    heading: {
      fontWeight: config.weights.bold,
      lineHeight: 1.38,
      marginTop: 0,
    },
    select: {
      width: '8vw',
      minWidth: 150,
      height: '3.5vh',
      minHeight: 40,
      '& .MuiButton-root.Mui-disabled': {
        color: (props) => {
          // @ts-expect-error TS(2339): Property 'from' does not exist on type '{}'.
          if (props.from === 'tab' && !props.readOnly) {
            return ` ${palette.tabs.profile} !important`;
          }
          return `${palette.select.disabled} !important`;
        },
        border: (props) => {
          // @ts-expect-error TS(2339): Property 'from' does not exist on type '{}'.
          if (props.from === 'tab' && !props.readOnly) {
            return ` 1px solid ${palette.tabs.profile} !important`;
          }
          return `1px solid ${palette.primary.contrastText} !important`;
        },
      },
    },
    cardContainer: {
      padding: '0px 10px 0px 10px',
    },
    leftBtn: {
      textAlign: 'right',
      paddingRight: 40,
    },
    contentContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      '& .MuiButton-fullWidth': {
        background: 'inherit !important',
      },
    },
    radioButtonsContainer: {
      width: '8vw',
      minWidth: 150,
      height: '3.5vh',
      minHeight: 40,
      paddingRight: '1.25rem',
      borderRadius: 5,
      border: `1px solid ${palette.primary.contrastText}`,
      '& .MuiFormControlLabel-root': {
        marginRight: 0,
      },
      '& .MuiFormControlLabel-label.Mui-disabled': {
        color: (props) => {
          // @ts-expect-error TS(2339): Property 'from' does not exist on type '{}'.
          if (props.from === 'tab' && !props.readOnly) {
            return palette.tabs.profile;
          }
        },
      },
      '& .MuiRadio-colorPrimary.Mui-disabled': {
        color: (props) => {
          // @ts-expect-error TS(2339): Property 'from' does not exist on type '{}'.
          if (props.from === 'tab' && !props.readOnly) {
            return palette.tabs.profile;
          }
        },
      },
    },
    buttonContainer: {
      borderTop: `1px solid ${config.colors.darkGreySoft}`,
      justifyContent: 'space-evenly',
      marginTop: '1.5rem !important',
      '& .MuiGrid-item': {
        padding: '0px 48px 0 0',
      },
    },
    cardContentContainer: {
      padding: '5px 0px 5px 0px !important',
      borderTop: `1px solid ${config.colors.darkGreySoft}`,
    },
    dropdownContainer: {
      width: '300px',
    },
    radioButtonsLabel: {
      marginRight: '3.33rem',
      padding: 0,
      width: '43vw',
      fontFamily: config.fonts.stackSans,
      fontWeight: 300,
      fontStretch: 'normal',
      fontStyle: 'normal',
      letterSpacing: 'normal',
      color: palette.text.text8,
      fontSize: (props) => {
        // @ts-expect-error TS(2339): Property 'from' does not exist on type '{}'.
        if (props.from === 'orgProfile') {
          return config.textSizes.tertia;
        }
        return config.textSizes.normal;
      },
    },
    inputFields: {
      width: '95%',
      float: 'left',
      margin: '0 0 2.5rem 0 !important',
      justifyContent: 'space-evenly',
      '& .MuiGrid-item': {
        padding: '2rem 0 0 8rem',
        display: 'flex',
      },
    },
    attestInput: {
      margin: '0 !important',
      overflow: 'visible',
      position: 'relative',
      padding: '1px',
      '& .MuiGrid-item': {
        padding: '.8rem 0 .2rem 0',
        display: 'flex',
      },
    },
    inactiveBackground: {
      overflow: 'hidden auto',
      padding: '2.5rem 0 0 0',
      backgroundColor: palette.background.paper,
      position: 'relative',
    },
    verifyTabIcon: {
      position: 'absolute',
      right: 0,
      top: '40%',
      cursor: 'pointer',
    },
    iconStyle: {
      paddingLeft: 5,
      paddingRight: 5,
      width: '45px',
      height: '70px',
    },
    rowContainer: {
      display: 'flex',
      flexDirection: 'column',
    },
    text: {
      fontSize: config.textSizes.tertia,
      color: palette.primary.contrastText,
      paddingLeft: (props) => {
        // @ts-expect-error TS(2339): Property 'from' does not exist on type '{}'.
        if (props.from === 'orgProfile') {
          return '8rem';
        }
        return 0;
      },
      fontWeight: config.weights.bold,
      textTransform: 'uppercase',
      marginTop: '2rem',
    },
    input: {
      fontFamily: config.fonts.stackSans,
      fontSize: config.textSizes.normal,
      lineHeight: 1.17,
      width: '100%',
      background: 'inherit',
      border: `solid 0.8px ${palette.primary.contrastText}`,
      borderRadius: 5,
      color: palette.select.disabled,
      padding: '0.416rem',
    },
    divider: {
      margin: '2rem 0',
    },
    editSection: {
      display: 'flex',

      '& > *': {
        marginLeft: spacing(1),
      },
    },
    toggleButton: {
      padding: '0.5rem 4rem',
    },
    toggleButtonDisabled: {
      color: `${palette.primary.contrastText} !important`,
    },
    parentBox: {
      border: `0.8px solid ${palette.text.secondary}`,
      borderRadius: 5,
      padding: '2rem',
      marginTop: '2rem',
    },
    pastIncidentDetails: {
      paddingLeft: '0 !important',
    },
  };
});

export default P250SecurityQuestions;
